import React, { useCallback, useMemo } from 'react'
import { Select as ShellSelect } from '@e3dc-react/shell/Elements/Select'
import { useField, useFormikContext } from 'formik'
import { makeStyles } from 'tss-react/mui'
import { ShellSelectProps } from '@e3dc-react/shell/Elements/Select/FeatureRichSelect'

interface SelectProps<T> extends Omit<ShellSelectProps<T>, 'label' | 'name' | 'onChange'> {
  label: string
  name: string
  onChange?: (field: string, value: T, shouldValidate: boolean) => void
}

const useStyles = makeStyles()(theme => ({
  select: {
    '& > div > div > div > p': {
      fontSize: theme.shape.select.fontSize,
    },
  },
}))

const Select = <T,>({
  label,
  name,
  options,
  onChange,
  disabled = false,
  className = '',
  required = false,
  isSearchable = false,
  clearable = false,
  hasVirtualList = false,
}: SelectProps<T>): React.ReactElement => {
  const [field, meta] = useField(name)
  const { setFieldValue } = useFormikContext()
  const { classes, cx } = useStyles()

  const hasError = useMemo<boolean>(
    () => {
      if (disabled) return false
      return Boolean(meta.error && (meta.initialValue !== meta.value || meta.touched))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name, meta]
  )

  const handleChange = useCallback(
    (event: T) => {
      onChange?.(name, event, true)
      setFieldValue(name, event, true)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name, onChange]
  )

  return (
    <ShellSelect
      label={label}
      value={field.value}
      options={options}
      onChange={handleChange}
      disabled={disabled}
      required={required}
      className={cx(classes.select, className)}
      helperText={hasError ? meta.error : undefined}
      useTooltipErrorMessage={false}
      menuShouldBlockScroll={false}
      isSearchable={isSearchable}
      clearable={clearable}
      error={hasError ? meta.error : undefined}
      hasVirtualList={hasVirtualList}
    />
  )
}

export default Select
